import React, { useEffect, useState } from "react";
import { connect } from "react-redux";
import { Link } from "react-router-dom";
import invoiceService from "../services/invoice";
import moment from "moment";
import { useTranslation } from "react-i18next";

function Invoices({ session }) {
    const { t } = useTranslation();
    const [invoices, setInvoices] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        if (session.token) {
            loadInvoices();
        }
    }, [session.token]);

    const loadInvoices = () => {
        invoiceService.getAll(session.token).then(res => {
            if (res.data) {
                setInvoices(res.data);
            }
            setLoading(false);
        }).catch(err => {
            console.error(err);
            setLoading(false);
        });
    };

    const deleteInvoice = async (id) => {
        if (!window.confirm(t("confirm_delete_invoice"))) return;

        const result = await invoiceService.delete(session.token, id);
        if (result.data) {
            setInvoices(invoices.filter(inv => inv._id !== id));
        }
    };

    const updateStatus = async (id, status) => {
        const result = await invoiceService.updateStatus(session.token, id, status);
        if (result.data) {
            setInvoices(invoices.map(inv => inv._id === id ? { ...inv, paymentStatus: status } : inv));
        }
    };

    if (loading) return <div className="text-center mt-5"><i className="fa fa-spinner fa-spin fa-3x"></i></div>;

    return (
        <div className="container">
            <div className="row mt-10">
                <div className="col">
                    <h2 className="mb-4">{t("invoices_management")}</h2>
                </div>
            </div>

            <div className="row mt-10">
                <div className="col">
                    <div className="card shadow-sm">
                        <div className="card-body p-0">
                            <div className="table-responsive">
                                <table className="table table-hover mb-0">
                                    <thead className="thead-light">
                                        <tr>
                                            <th>{t("col_date")}</th>
                                            <th>{t("col_invoice_number")}</th>
                                            <th>{t("col_creator")}</th>
                                            <th>{t("col_receiver")}</th>
                                            <th>{t("col_ip_address")}</th>
                                            <th>{t("col_views")}</th>
                                            <th>{t("col_total")}</th>
                                            <th>{t("col_status")}</th>
                                            <th>{t("col_action")}</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {invoices.map(inv => (
                                            <tr key={inv._id}>
                                                <td>{moment(inv.date).format("YYYY-MM-DD")}</td>
                                                <td><a href={`/invoices/${inv._id}`} target="_blank" rel="noopener noreferrer">{inv.invoiceNumber}</a></td>
                                                <td>
                                                    {inv.user ? (
                                                        <div className="d-flex flex-column">
                                                            <span className="font-weight-bold text-dark">{inv.user.name || inv.user.businessName || t("no_name")}</span>
                                                            <span className="text-muted small">{inv.user.email}</span>
                                                        </div>
                                                    ) : (
                                                        <span className="text-muted">{t("unknown")}</span>
                                                    )}
                                                </td>
                                                <td>{inv.receiverName || t("na")}</td>
                                                <td><span className="badge badge-light">{inv.ipAddress || t("na")}</span></td>
                                                <td><span className="badge badge-info">{inv.views || 0}</span></td>
                                                <td>{inv.total.toLocaleString()} {inv.currency || "USD"}</td>
                                                <td>
                                                    <span className={`badge badge-${inv.paymentStatus === 'paid' ? 'success' : (inv.paymentStatus === 'overdue' ? 'danger' : (inv.paymentStatus === 'void' ? 'secondary' : 'warning'))}`}>
                                                        {inv.paymentStatus}
                                                    </span>
                                                </td>
                                                <td>
                                                    <div className="btn-group btn-group-sm">
                                                        <button className={`btn btn-outline-${inv.paymentStatus === 'paid' ? 'secondary' : 'success'}`}
                                                            onClick={() => updateStatus(inv._id, 'paid')} disabled={inv.paymentStatus === 'paid'} title={t("mark_as_paid")}>
                                                            <i className="fa fa-check"></i>
                                                        </button>
                                                        <button className={`btn btn-outline-${inv.paymentStatus === 'unpaid' ? 'secondary' : 'warning'}`}
                                                            onClick={() => updateStatus(inv._id, 'unpaid')} disabled={inv.paymentStatus === 'unpaid'} title={t("mark_as_unpaid")}>
                                                            <i className="fa fa-clock"></i>
                                                        </button>
                                                        <button className={`btn btn-outline-${inv.paymentStatus === 'void' ? 'secondary' : 'dark'}`}
                                                            onClick={() => updateStatus(inv._id, 'void')} disabled={inv.paymentStatus === 'void'} title={t("void")}>
                                                            <i className="fa fa-ban"></i>
                                                        </button>
                                                        <a href={`https://alshorja.com/invoices/${inv._id}`} target="_blank" className="btn btn-outline-primary" title={t("btn_view")} rel="noopener noreferrer">
                                                            <i className="fa fa-eye"></i>
                                                        </a>
                                                        <button className="btn btn-outline-danger" onClick={() => deleteInvoice(inv._id)} title={t("btn_delete")}>
                                                            <i className="fa fa-trash"></i>
                                                        </button>
                                                    </div>
                                                </td>
                                            </tr>
                                        ))}
                                        {invoices.length === 0 && (
                                            <tr>
                                                <td colSpan="8" className="text-center p-4">{t("no_invoices")}</td>
                                            </tr>
                                        )}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}

const mapStateToProps = state => ({
    session: state.session
});

export default connect(mapStateToProps)(Invoices);
